/**
 * "Sair da viagem" — quem participa deixa o grupo por conta própria (PO,
 * 19/09/2026). Sempre com confirmação; nada é apagado: o que a pessoa lançou
 * fica na viagem, ela só deixa de ver. O dono não sai — transfere antes
 * (a API recusa com `owner_cannot_leave`).
 *
 * Duas portas, um componente: a linha em Minhas viagens (botão de ícone) e o
 * rodapé de Configurações da viagem (botão largo).
 */
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '../../auth/store';
import { esquecerViagemAtiva, lerViagemAtiva } from '../../trip/viagemAtiva';
import { Icon } from '../../ui/icons';
import { Sheet } from '../../ui/Sheet';

export function SairDaViagem({ tripId, nome, formato }: { tripId: string; nome: string; formato: 'icone' | 'largo' }) {
  const navegar = useNavigate();
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id ?? '');
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  async function sair() {
    setOcupado(true);
    setErro(null);
    try {
      const res = await fetch(`/api/v1/trips/${tripId}/leave`, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: '{}' });
      if (!res.ok) {
        const corpo = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(corpo.message ?? `Falha (${res.status})`);
      }
      // Se era a viagem ativa deste aparelho, esquece — senão a Home tentaria abri-la.
      if (lerViagemAtiva(userId)?.id === tripId) esquecerViagemAtiva();
      await qc.invalidateQueries({ queryKey: ['viagens'] });
      setAberto(false);
      navegar('/viagens', { replace: true, state: { aviso: `Você saiu de ${nome}.` } });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não foi possível sair agora.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      {formato === 'icone' ? (
        <button type="button" className="viagens__arquivar" aria-label={`Sair de ${nome}`} onClick={() => setAberto(true)} data-testid="viagem-sair">
          <Icon name="sair" size={18} />
        </button>
      ) : (
        <button type="button" className="nv-btn nv-btn--ghost-danger nv-btn--block" onClick={() => setAberto(true)} data-testid="viagem-sair">
          <Icon name="sair" size={18} /> Sair da viagem
        </button>
      )}
      {aberto && (
        <Sheet titleId="viagem-sair-titulo" onFechar={() => setAberto(false)} testId="viagem-sair-sheet">
          <h2 className="sheet__title" id="viagem-sair-titulo">Sair desta viagem?</h2>
          <p className="sheet__text">
            Você deixa de ver <b>{nome}</b> — roteiro, gastos e fotos. O que você lançou continua lá para o grupo. Para voltar, precisa de um convite novo.
          </p>
          {erro && (
            <p className="auth-note sug__erro" role="alert">
              {erro}
            </p>
          )}
          <div className="sheet__acoes">
            <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={() => setAberto(false)} disabled={ocupado}>
              Ficar
            </button>
            <button type="button" className="nv-btn nv-btn--danger nv-btn--block" onClick={() => void sair()} disabled={ocupado} data-testid="viagem-sair-confirma">
              {ocupado ? 'Saindo…' : 'Sair da viagem'}
            </button>
          </div>
        </Sheet>
      )}
    </>
  );
}
